import React, { useState, useEffect } from 'react'
import constants from '../../../../config/constants'
import { Formik } from 'formik'
import * as yup from 'yup'
import { Form, Col } from 'react-bootstrap'
import { Vendor } from '../../../../models/Vendor'
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input'
import { toast } from 'react-toastify'
import { uploadFile } from '../../../../services/genericService'
import { Country } from '../../../../models/Country'

interface EditCompanyDetailsFormProps {
  companyProfile?: Vendor
  countries: Array<Country>
  callback: (
    countryId: string,
    logoUrl?: string,
    email?: string,
    phonenumber?: string,
    description?: string
  ) => void
  isSubmitting: boolean
}

const msg = constants.inlineError
const schema = yup.object({
  email: yup.string().email(msg.auth.invalidEmail),
  phonenumber: yup
    .string()
    .test('ValidPhonenumber', msg.auth.invalidPhonenumber, value => {
      return isPossiblePhoneNumber(value)
    }),
  description: yup.string().max(2000, msg.profile.invalidDescription)
})

const EditCompanyDetailsForm: React.FC<EditCompanyDetailsFormProps> = ({
  companyProfile,
  countries,
  callback,
  isSubmitting
}) => {
  const [isValidNumber, setIsValidNumber] = useState(() => {
    return isPossiblePhoneNumber(companyProfile?.phonenumber || '')
  })
  const [countryOptions, setCountryOptions] = useState<Array<JSX.Element>>([])

  useEffect(() => {
    let options: Array<JSX.Element> = []
    countries.forEach(country => {
      options.push(
        <option key={country.id} value={country.id} data-testid="countryOption">
          {country.name}
        </option>
      )
    })
    setCountryOptions(options)
  }, [countries])

  return (
    <Formik
      validationSchema={schema}
      onSubmit={values => {
        callback(
          values.countryId,
          values.logoUrl,
          values.email,
          values.phonenumber,
          values.description
        )
      }}
      initialValues={{
        logoUrl: companyProfile?.logoUrl || '',
        name: companyProfile?.name || '',
        email: companyProfile?.email || '',
        phonenumber: companyProfile?.phonenumber || '+65',
        description: companyProfile?.bio || '',
        countryId:
          companyProfile?.originCountry?.id ||
          (countries.length ? countries[0].id : '')
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Row>
            <Form.Group as={Col} md="auto">
              <img
                width="100"
                height="100"
                src={values.logoUrl || '/image.png'}
                alt="Company logo"
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label htmlFor="edit-profile-avatar">Upload logo</Form.Label>
              <Form.File>
                <Form.File.Input
                  className="pb-lg-3"
                  id="edit-profile-avatar"
                  name="logoUrl"
                  accept="image/*"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const logo = e.target.files![0]
                    toast.info(constants.alertMessage.uploadingImage, {
                      autoClose: false
                    })
                    uploadFile(logo, 'avatars').then(url => {
                      setFieldValue('logoUrl', url)
                      toast.dismiss()
                    })
                  }}
                />
              </Form.File>
            </Form.Group>
          </Form.Row>
          <Form.Group>
            <Form.Label htmlFor="edit-profile-name">Company Name</Form.Label>
            <Form.Control
              id="edit-profile-name"
              type="text"
              name="name"
              value={values.name}
              readOnly
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="edit-profile-country">
              Delivery Country
            </Form.Label>
            <Form.Control
              id="edit-profile-country"
              as="select"
              custom
              name="countryId"
              placeholder="Select country"
              value={values.countryId}
              onChange={handleChange}
            >
              {countryOptions}
            </Form.Control>
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="edit-profile-email">Company Email</Form.Label>
            <Form.Control
              id="edit-profile-email"
              type="email"
              name="email"
              placeholder="Enter company email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.email && !!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label
              className="mochi-sign-in-label"
              htmlFor="edit-profile-phonenumber"
            >
              Company phone number
            </Form.Label>
            <PhoneInput
              id="edit-profile-phonenumber"
              international
              defaultCountry="SG"
              value={values.phonenumber}
              className={
                'form-control' +
                (touched.phonenumber ? ' touched' : '') +
                (isValidNumber ? '' : ' invalid')
              }
              onChange={number => {
                setFieldValue('phonenumber', number)
                setFieldTouched('phonenumber')
                setIsValidNumber(isPossiblePhoneNumber(number))
              }}
            />
            {touched.phonenumber && !isValidNumber && (
              <div className="invalid-feedback" style={{ display: 'block' }}>
                {msg.auth.invalidPhonenumber}
              </div>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="edit-profile-description">
              Description
            </Form.Label>
            <Form.Control
              id="edit-profile-description"
              as="textarea"
              name="description"
              placeholder="Enter description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.description && !!errors.description}
            />
            <Form.Control.Feedback type="invalid">
              {errors.description}
            </Form.Control.Feedback>
          </Form.Group>
          <div className="text-center mt-4">
            <button
              className="mochi-button orange"
              type="submit"
              data-testid="edit-profile-submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default EditCompanyDetailsForm
